import { ROUTES } from 'consts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePlanLimits from 'hooks/usePlanLimits'
import useQueryParams from 'hooks/useQueryParams'
import queryString from 'query-string'
import { useMemo } from 'react'
import { isCopilotDoctorOverride } from './useCopilotAccountOwnerId'

export const CopilotReports = {
  Copilot: 'copilot',
  OralHygiene: 'oralHygiene',
  TxTracking: 'txTracking'
}

/**
 * A report path that keeps an internal user's doctor override. Without it the destination report
 * falls back to the signed-in account and its guard can redirect again.
 */
export const buildCopilotReportPath = (route, copilotDoctorId) =>
  copilotDoctorId ? `${route}?${queryString.stringify({ copilotDoctorId })}` : route

/**
 * The Copilot reports the selected account owner can open, in rail order.
 * Each report sits behind its own practice feature flag. Copilot and Tx Tracking also require an
 * HI plan; Oral Hygiene does not (GR-10760), so a non-HI account with only that flag gets a
 * single-report Copilot area.
 *
 * An internal user's doctor override (`?copilotDoctorId=`) offers all three reports. The flags and
 * plan resolved here belong to the signed-in internal user's context, and the viewed practice's are
 * never loaded, so gating on them hid reports at random: an Admin or Sales user with no selected
 * practice got no tabs at all. The report guards already skip their redirects for the override.
 */
export default () => {
  const {
    copilotControl: copilotControlFF,
    oralHygieneReport: oralHygieneReportFF,
    txTrackingReport: txTrackingReportFF
  } = useFeatureFlags()
  const { isHI } = usePlanLimits()
  const { queryParams } = useQueryParams()
  const isDoctorOverride = isCopilotDoctorOverride(queryParams)
  const meetsHIRequirement = !!isHI

  return useMemo(
    () =>
      [
        {
          key: CopilotReports.Copilot,
          route: ROUTES.COPILOT_CONTROL,
          isEnabled: copilotControlFF && meetsHIRequirement
        },
        { key: CopilotReports.OralHygiene, route: ROUTES.COPILOT_ORAL_HYGIENE, isEnabled: oralHygieneReportFF },
        {
          key: CopilotReports.TxTracking,
          route: ROUTES.COPILOT_TX_TRACKING,
          isEnabled: txTrackingReportFF && meetsHIRequirement
        }
      ].filter(report => isDoctorOverride || !!report.isEnabled),
    [copilotControlFF, oralHygieneReportFF, txTrackingReportFF, meetsHIRequirement, isDoctorOverride]
  )
}
